import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CloudOff, GitPullRequest, Loader2, Save, Send, Settings2, Sparkles, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  normalizeContent,
  type DocumentContent,
  type DocumentDetailDto,
  type Section,
  type TemplateFieldDef,
  type UserSummary,
  type VersionDto,
} from '@imsenta/shared';
import { PageHeader } from '@/components/AppShell';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  TagInput,
  Textarea,
  useToast,
} from '@/components/ui';
import { AiAssistPanel } from '@/components/AiAssistPanel';
import { ProposeChangeModal } from '@/components/ProposeChangeModal';
import { DocumentEditor } from '@/editor/DocumentEditor';
import { validateContent } from '@/editor/sections';
import { api, ApiError } from '@/lib/api';
import { fmtRelative } from '@/lib/format';
import { useSession } from '@/lib/session';

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

const AUTOSAVE_DELAY_MS = 2500;

/** Stable fingerprint of the editable state, used to detect real changes. */
function snapshotOf(content: DocumentContent, title: string, metadata: Record<string, unknown>): string {
  return JSON.stringify({ content, title, metadata });
}

export function DocumentEditPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { push } = useToast();
  const { can } = useSession();

  const [content, setContent] = useState<DocumentContent | null>(null);
  const [title, setTitle] = useState('');
  const [metadata, setMetadata] = useState<Record<string, unknown>>({});
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [baseVersionId, setBaseVersionId] = useState<string | undefined>();
  const [propertiesOpen, setPropertiesOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [assistSection, setAssistSection] = useState<Section | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [proposeOpen, setProposeOpen] = useState(false);
  /** True once the API has told us direct edits need to go through governance. */
  const [mustPropose, setMustPropose] = useState(false);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * Snapshot of what the server holds. The editor normalises content as it
   * mounts (adding an empty paragraph, filling in default attributes), which
   * would otherwise register as an edit and show "Unsaved changes" the moment
   * a document is opened.
   */
  const savedSnapshot = useRef<string>('');

  const { data: doc, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: () => api.get<DocumentDetailDto>(`/documents/${id}`),
  });

  const { data: aiStatus } = useQuery({
    queryKey: ['ai-status'],
    queryFn: () => api.get<{ enabled: boolean; reason: string | null; model: string }>('/ai/status'),
    retry: false,
  });

  const { data: members } = useQuery({
    queryKey: ['members'],
    queryFn: () => api.get<{ items: UserSummary[] }>('/tenant/members'),
    retry: false,
  });

  // Load the working copy once, then keep it entirely client-side until saved.
  useEffect(() => {
    if (!doc || content) return;
    const source = doc.draft ?? doc.published;
    setContent(normalizeContent(source?.content ?? { schemaVersion: 1, sections: [] }));
    setTitle(source?.title ?? doc.title);
    setMetadata(doc.metadata ?? {});
    setBaseVersionId(doc.draft?.id);
    setLastSaved(doc.draft?.createdAt ?? null);
    savedSnapshot.current = snapshotOf(
      normalizeContent(source?.content ?? { schemaVersion: 1, sections: [] }),
      source?.title ?? doc.title,
      doc.metadata ?? {},
    );
  }, [doc, content]);

  const save = useMutation({
    mutationFn: (payload: { content: DocumentContent; title: string; metadata: Record<string, unknown> }) =>
      api.put<VersionDto>(`/documents/${id}/draft`, {
        content: payload.content,
        title: payload.title,
        metadata: payload.metadata,
        baseVersionId,
      }),
    onSuccess: (version, variables) => {
      savedSnapshot.current = snapshotOf(variables.content, variables.title, variables.metadata);
      setSaveState('saved');
      setSaveError(null);
      setLastSaved(new Date().toISOString());
      setBaseVersionId(version.id);
      // Without this the document page shows pre-save content and no Publish
      // button until the query goes stale.
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => {
      setSaveState('error');
      if (error instanceof ApiError && error.status === 403) {
        // The organisation requires a change request for published documents.
        setMustPropose(true);
        setSaveError(null);
        return;
      }
      setSaveError(error instanceof ApiError ? error.message : 'Could not save your changes');
    },
  });

  const flush = useCallback(() => {
    if (!content) return;
    if (timer.current) clearTimeout(timer.current);
    setSaveState('saving');
    save.mutate({ content, title, metadata });
  }, [content, title, metadata, save]);

  /** Called after any edit; no-ops when the content matches what was saved. */
  const markDirty = useCallback(() => {
    setSaveState('dirty');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setSaveState('saving');
      // Read the latest values at fire time rather than closing over stale ones.
      setContent((currentContent) => {
        if (currentContent) {
          setTitle((currentTitle) => {
            setMetadata((currentMeta) => {
              save.mutate({ content: currentContent, title: currentTitle, metadata: currentMeta });
              return currentMeta;
            });
            return currentTitle;
          });
        }
        return currentContent;
      });
    }, AUTOSAVE_DELAY_MS);
  }, [save]);

  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  // Warn before losing unsaved work.
  useEffect(() => {
    const handler = (event: BeforeUnloadEvent) => {
      if (saveState === 'dirty' || saveState === 'saving') {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saveState]);

  // Ctrl/Cmd+S saves immediately.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        flush();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [flush]);

  const discard = useMutation({
    mutationFn: () => api.del(`/documents/${id}/draft`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      push('Draft discarded', 'success');
      navigate(`/documents/${id}`);
    },
  });

  const problems = useMemo(() => (content ? validateContent(content) : []), [content]);
  const templateFields: TemplateFieldDef[] = doc?.template?.fields ?? [];

  if (isLoading || !doc || !content) return <Spinner label="Opening the editor…" />;

  if (!can('document:edit')) {
    return (
      <Card>
        <Alert tone="warning" title="You cannot edit this document">
          Your role does not allow editing. You can propose a change instead.
        </Alert>
      </Card>
    );
  }

  return (
    <>
      <PageHeader
        back={{ to: `/documents/${id}`, label: doc.title }}
        title={
          <input
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (content && snapshotOf(content, e.target.value, metadata) !== savedSnapshot.current) markDirty();
            }}
            className="w-full min-w-0 rounded border border-transparent bg-transparent px-1 py-0.5 text-xl font-semibold tracking-tight outline-none hover:border-ink-200 focus:border-brand-400 focus:bg-white"
            placeholder="Document title"
          />
        }
        meta={
          <>
            <Badge tone="neutral">{doc.reference}</Badge>
            <Badge tone="brand">{doc.documentTypeName}</Badge>
            <SaveIndicator state={saveState} lastSaved={lastSaved} />
          </>
        }
        actions={
          <>
            <Button icon={<Settings2 className="size-4" />} onClick={() => setPropertiesOpen(true)}>
              Properties
            </Button>
            {doc.draft ? (
              <Button icon={<Undo2 className="size-4" />} onClick={() => setDiscardOpen(true)}>
                Discard draft
              </Button>
            ) : null}
            <Button icon={<Save className="size-4" />} loading={saveState === 'saving'} onClick={flush}>
              Save
            </Button>
            {doc.published && can('changeRequest:create') ? (
              <Button
                variant={mustPropose ? 'primary' : 'secondary'}
                icon={<GitPullRequest className="size-4" />}
                onClick={() => setProposeOpen(true)}
              >
                Propose as change
              </Button>
            ) : null}
            {can('document:publish') && !mustPropose ? (
              <Button
                variant="success"
                icon={<Send className="size-4" />}
                onClick={() => {
                  flush();
                  navigate(`/documents/${id}`);
                  push('Draft saved. Use Publish on the document page to issue it.', 'info');
                }}
              >
                Finish
              </Button>
            ) : null}
          </>
        }
      />

      {mustPropose ? (
        <div className="mb-4">
          <Alert tone="info" title="This document is published">
            Your organisation requires changes to published documents to go through review. Keep editing here — when you are
            ready, choose{' '}
            <button type="button" onClick={() => setProposeOpen(true)} className="font-medium underline">
              Propose as change
            </button>{' '}
            and your edits become the proposal. Nothing is lost in the meantime.
          </Alert>
        </div>
      ) : null}

      {saveError ? (
        <div className="mb-4">
          <Alert tone="danger" title="Your last save did not go through">
            {saveError}{' '}
            <button type="button" onClick={flush} className="font-medium underline">
              Try again
            </button>
          </Alert>
        </div>
      ) : null}

      {problems.length ? (
        <div className="mb-4">
          <Alert tone="warning" title="Before you publish">
            <ul className="list-inside list-disc">
              {problems.slice(0, 5).map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </Alert>
        </div>
      ) : null}

      <DocumentEditor
        content={content}
        documentId={doc.id}
        aiAvailable={aiStatus?.enabled}
        onAssist={(section) => setAssistSection(section)}
        onChange={(next) => {
          setContent(next);
          if (snapshotOf(next, title, metadata) !== savedSnapshot.current) markDirty();
        }}
      />

      {content ? (
        <ProposeChangeModal
          open={proposeOpen}
          onClose={() => setProposeOpen(false)}
          documentId={doc.id}
          documentTitle={doc.title}
          content={content}
          title={title}
          metadata={metadata}
          onCreated={(changeRequestId) => navigate(`/change-requests/${changeRequestId}`)}
        />
      ) : null}

      <PropertiesModal
        open={propertiesOpen}
        onClose={() => setPropertiesOpen(false)}
        doc={doc}
        metadata={metadata}
        fields={templateFields}
        members={members?.items ?? []}
        onSave={(nextMetadata, summary, ownerId) => {
          setMetadata(nextMetadata);
          void api
            .patch(`/documents/${id}`, { metadata: nextMetadata, summary, ownerId })
            .then(() => queryClient.invalidateQueries({ queryKey: ['document', id] }));
          markDirty();
          setPropertiesOpen(false);
        }}
      />

      <ConfirmDialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        onConfirm={() => discard.mutate()}
        title="Discard this draft?"
        message="All unpublished changes will be lost and the document will revert to its published version."
        confirmLabel="Discard draft"
        loading={discard.isPending}
      />

      {assistSection ? (
        <AiAssistPanel
          section={assistSection}
          documentId={doc.id}
          documentType={doc.documentTypeName}
          onClose={() => setAssistSection(null)}
          onInsert={(nodes) => {
            setContent((current) => {
              if (!current) return current;
              const apply = (sections: Section[]): Section[] =>
                sections.map((section) =>
                  section.id === assistSection.id
                    ? { ...section, body: { ...section.body, content: [...section.body.content, ...nodes] } }
                    : { ...section, children: apply(section.children) },
                );
              return { ...current, sections: apply(current.sections) };
            });
            markDirty();
            setAssistSection(null);
            push('Draft inserted. Review it before publishing.', 'success');
          }}
        />
      ) : null}
    </>
  );
}

function SaveIndicator({ state, lastSaved }: { state: SaveState; lastSaved: string | null }) {
  if (state === 'saving') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-500">
        <Loader2 className="size-3.5 animate-spin" /> Saving…
      </span>
    );
  }
  if (state === 'error') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-rose-600">
        <CloudOff className="size-3.5" /> Not saved
      </span>
    );
  }
  if (state === 'dirty') return <span className="text-[12.5px] text-amber-600">Unsaved changes</span>;
  if (state === 'saved') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[12.5px] text-emerald-600">
        <Check className="size-3.5" /> Saved
      </span>
    );
  }
  return lastSaved ? <span className="text-[12.5px] text-ink-400">Last saved {fmtRelative(lastSaved)}</span> : null;
}

function PropertiesModal({
  open,
  onClose,
  doc,
  metadata,
  fields,
  members,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  doc: DocumentDetailDto;
  metadata: Record<string, unknown>;
  fields: TemplateFieldDef[];
  members: UserSummary[];
  onSave: (metadata: Record<string, unknown>, summary: string, ownerId: string | null) => void;
}) {
  const [draft, setDraft] = useState<Record<string, unknown>>(metadata);
  const [summary, setSummary] = useState(doc.summary ?? '');
  const [ownerId, setOwnerId] = useState(doc.owner?.id ?? '');

  useEffect(() => {
    if (open) {
      setDraft(metadata);
      setSummary(doc.summary ?? '');
      setOwnerId(doc.owner?.id ?? '');
    }
  }, [open, metadata, doc]);

  const set = (key: string, value: unknown) => setDraft((current) => ({ ...current, [key]: value }));

  // Always offer the standard governance fields, plus anything the template adds.
  const standard: TemplateFieldDef[] = [
    { key: 'owner', label: 'Document owner (role)', type: 'text', help: 'The role accountable for keeping this current.' },
    { key: 'approver', label: 'Approver', type: 'text' },
    { key: 'classification', label: 'Classification', type: 'select', options: ['public', 'internal', 'confidential', 'restricted'] },
    { key: 'effective_from', label: 'Effective from', type: 'date' },
    { key: 'review_due', label: 'Next review due', type: 'date' },
    { key: 'tags', label: 'Tags', type: 'tags' },
  ];
  const extra = fields.filter((f) => !standard.some((s) => s.key === f.key));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Document properties"
      description="These values are exported as YAML front matter and shown on the PDF cover."
      width="lg"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={() => onSave(draft, summary, ownerId || null)}>
            Apply
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Summary" hint="One line shown in listings and the catalog.">
          <Input value={summary} onChange={(e) => setSummary(e.target.value)} />
        </Field>

        <Field label="Owner (user account)" hint="Receives review reminders and is shown as the responsible person.">
          <Select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
            <option value="">Unassigned</option>
            {members.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name} · {member.email}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          {[...standard, ...extra].map((field) => (
            <Field key={field.key} label={field.label} hint={field.help} className={field.type === 'tags' || field.type === 'textarea' ? 'sm:col-span-2' : ''}>
              {field.type === 'select' ? (
                <Select value={String(draft[field.key] ?? '')} onChange={(e) => set(field.key, e.target.value)}>
                  <option value="">Not set</option>
                  {(field.options ?? []).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              ) : field.type === 'tags' ? (
                <TagInput
                  value={Array.isArray(draft[field.key]) ? (draft[field.key] as string[]) : []}
                  onChange={(next) => set(field.key, next)}
                  placeholder="iso27001, gdpr…"
                />
              ) : field.type === 'textarea' ? (
                <Textarea rows={3} value={String(draft[field.key] ?? '')} onChange={(e) => set(field.key, e.target.value)} />
              ) : (
                <Input
                  type={field.type === 'date' ? 'date' : 'text'}
                  value={String(draft[field.key] ?? '')}
                  onChange={(e) => set(field.key, e.target.value)}
                />
              )}
            </Field>
          ))}
        </div>
      </div>
    </Modal>
  );
}
